import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { apiRequest } from "services/tenantUserAdminService";
import { TC } from "../../tokens/text";

// ─── Chart of Accounts (UK standard) ─────────────────────────────────────────

const ACCOUNTS = [
  { code: "1200", label: "1200 · Bank Account" },
  { code: "4000", label: "4000 · Sales Income" },
  { code: "4999", label: "4999 · Unclassified Income" },
  { code: "5000", label: "5000 · Cost of Goods Sold" },
  { code: "7000", label: "7000 · Wages & Salaries" },
  { code: "7100", label: "7100 · Rent & Office" },
  { code: "7200", label: "7200 · Utilities" },
  { code: "7300", label: "7300 · Phone & Broadband" },
  { code: "7400", label: "7400 · Software & Subscriptions" },
  { code: "7500", label: "7500 · Travel & Transport" },
  { code: "7600", label: "7600 · Meals & Entertainment" },
  { code: "7800", label: "7800 · Bank Charges & Fees" },
  { code: "7900", label: "7900 · Tax Payments" },
  { code: "2201", label: "2201 · VAT Liability" },
  { code: "9999", label: "9999 · Unclassified Expense" },
];

const TAX_TREATMENTS = [
  { value: "standard_20",       label: "Standard Rate 20%" },
  { value: "reduced_5",         label: "Reduced Rate 5%" },
  { value: "zero_rated",        label: "Zero Rated" },
  { value: "exempt",            label: "Exempt" },
  { value: "reverse_charge_eu", label: "Reverse Charge (EU)" },
  { value: "vat_payment",       label: "VAT Payment" },
  { value: "unknown",           label: "Unknown / To Confirm" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function accountLabel(code) {
  return ACCOUNTS.find((a) => a.code === code)?.label || code || "—";
}

function fmtAmount(amount, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount || 0);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function ConfidenceBadge({ score }) {
  const pct = Math.round((score ?? 0) * 100);
  const cfg = score == null
    ? { label: "Unscored",        bg: "rgba(255,255,255,0.12)" }
    : pct >= 95
      ? { label: `${pct}% · Auto`,    bg: "#01b574" }
      : pct >= 80
        ? { label: `${pct}% · Flagged`, bg: "#0075ff" }
        : { label: `${pct}% · Review`,  bg: "#f44336" };

  return (
    <VuiBadge variant="standard" badgeContent={cfg.label} size="xs" container
      sx={({ borders: { borderRadius, borderWidth }, palette }) => ({
        background: cfg.bg, border: `${borderWidth[1]} solid transparent`,
        borderRadius: borderRadius.md, color: palette.white.main, whiteSpace: "nowrap",
      })}
    />
  );
}

function StatusBadge({ status }) {
  const cfg = {
    pending_review: { label: "Needs Review", bg: "#f44336" },
    flagged:        { label: "Flagged",       bg: "#0075ff" },
    auto_posted:    { label: "Auto-Posted",   bg: "#01b574" },
    overridden:     { label: "Overridden",    bg: "rgba(245,166,35,0.85)" },
  }[status] || { label: status, bg: "rgba(255,255,255,0.12)" };

  return (
    <VuiBadge variant="standard" badgeContent={cfg.label} size="xs" container
      sx={({ borders: { borderRadius, borderWidth }, palette }) => ({
        background: cfg.bg, border: `${borderWidth[1]} solid transparent`,
        borderRadius: borderRadius.md, color: palette.white.main, whiteSpace: "nowrap",
      })}
    />
  );
}

const DIALOG_SX = {
  backgroundImage: "none", backgroundColor: "rgba(15,18,59,0.98)",
  color: TC.primary, border: "1px solid #d8e6f7", borderRadius: "16px",
};

const selectSx = {
  color: TC.primary, fontSize: "0.875rem",
  background: "#f4f8ff",
  border: "1px solid #d8e6f7", borderRadius: "8px",
  "& .MuiSelect-icon": { color: TC.secondary },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

const menuProps = {
  PaperProps: {
    sx: {
      background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", color: TC.primary,
      "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
      "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.25)" },
    },
  },
};

// ─── Override Dialog ──────────────────────────────────────────────────────────

function OverrideDialog({ open, onClose, txn, tenantId, onSaved }) {
  const [form, setForm]         = useState({ debitAccount: "", creditAccount: "", taxTreatment: "unknown" });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (open && txn) {
      setForm({
        debitAccount:  txn.suggested_debit_account  || "9999",
        creditAccount: txn.suggested_credit_account || "1200",
        taxTreatment:  txn.tax_treatment            || "unknown",
      });
      setError("");
    }
  }, [open, txn]);

  const handleSave = async () => {
    if (!form.debitAccount || !form.creditAccount) { setError("Both accounts are required."); return; }
    setIsSaving(true); setError("");
    try {
      await apiRequest(`/tenant/${tenantId}/transactions/${txn.id}/correct`, {
        method: "POST",
        body: form,
      });
      onSaved(txn.id);
    } catch (err) {
      setError(err?.message || "Failed to save correction.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!txn) return null;
  return (
    <Dialog
      open={open}
      onClose={() => !isSaving && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="exceptions-override-dialog-title"
      aria-describedby="exceptions-override-dialog-description"
      PaperProps={{ sx: DIALOG_SX }}
    >
      <DialogTitle id="exceptions-override-dialog-title" sx={{ color: TC.primary, pb: 0.5 }}>
        Override Classification
        <VuiTypography id="exceptions-override-dialog-description" variant="caption" display="block" mt={0.3} sx={{ color: TC.secondary }}>
          {txn.counterparty?.name || "Unknown"} · {fmtAmount(txn.amount, txn.currency)} · {fmtDate(txn.transaction_date)}
        </VuiTypography>
      </DialogTitle>

      <DialogContent dividers sx={{ bordercolor: "#7a94b0" }}>
        {error && <VuiTypography color="error" variant="caption" display="block" mb={1.5}>{error}</VuiTypography>}

        {txn.categorisation_explanation && (
          <VuiBox mb={2} p={1.5} borderRadius="8px"
            sx={{ background: "rgba(0,117,255,0.07)", border: "1px solid rgba(0,117,255,0.2)" }}>
            <VuiTypography variant="caption" display="block" mb={0.3} sx={{ color: TC.secondary }}>AI Suggestion</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{txn.categorisation_explanation}</VuiTypography>
            <VuiBox mt={0.75}><ConfidenceBadge score={txn.confidence_score} /></VuiBox>
          </VuiBox>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Debit Account *</VuiTypography>
            <FormControl fullWidth size="small">
              <Select value={form.debitAccount}
                onChange={(e) => setForm((p) => ({ ...p, debitAccount: e.target.value }))}
                sx={selectSx} MenuProps={menuProps}>
                {ACCOUNTS.map((a) => <MenuItem key={a.code} value={a.code} sx={{ fontSize: "0.875rem" }}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Credit Account *</VuiTypography>
            <FormControl fullWidth size="small">
              <Select value={form.creditAccount}
                onChange={(e) => setForm((p) => ({ ...p, creditAccount: e.target.value }))}
                sx={selectSx} MenuProps={menuProps}>
                {ACCOUNTS.map((a) => <MenuItem key={a.code} value={a.code} sx={{ fontSize: "0.875rem" }}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>VAT / Tax Treatment</VuiTypography>
            <FormControl fullWidth size="small">
              <Select value={form.taxTreatment}
                onChange={(e) => setForm((p) => ({ ...p, taxTreatment: e.target.value }))}
                sx={selectSx} MenuProps={menuProps}>
                {TAX_TREATMENTS.map((t) => <MenuItem key={t.value} value={t.value} sx={{ fontSize: "0.875rem" }}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose} disabled={isSaving}>Cancel</VuiButton>
        <VuiButton color="success" size="small" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving…" : "Apply Correction"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function Exceptions() {
  const { tenantId } = useAuthz();
  const [exceptions, setExceptions]   = useState([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [loadError, setLoadError]     = useState("");
  const [overrideTxn, setOverrideTxn] = useState(null);
  const [successMsg, setSuccessMsg]   = useState("");

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 4000); };

  const load = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true); setLoadError("");
    try {
      const data = await apiRequest(`/tenant/${tenantId}/exceptions?limit=100`, { method: "GET" });
      setExceptions(Array.isArray(data?.exceptions) ? data.exceptions : []);
    } catch (err) {
      setLoadError(err?.message || "Failed to load exceptions.");
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (txn) => {
    try {
      await apiRequest(`/tenant/${tenantId}/transactions/${txn.id}/categorise`, {
        method: "POST",
        body: {
          debitAccount:  txn.suggested_debit_account  || "9999",
          creditAccount: txn.suggested_credit_account || "1200",
          taxTreatment:  txn.tax_treatment,
        },
      });
      flash(`Approved: ${txn.counterparty?.name || txn.id}`);
      load();
    } catch (err) { flash(`Error: ${err?.message}`); }
  };

  const pendingCount = exceptions.filter((e) => e.categorisation_status === "pending_review").length;
  const flaggedCount = exceptions.filter((e) => e.categorisation_status === "flagged").length;

  const columns = [
    { name: "date",            align: "left",   width: "90px"  },
    { name: "counterparty",    align: "left"                   },
    { name: "amount",          align: "right",  width: "110px" },
    { name: "ai suggestion",   align: "left"                   },
    { name: "confidence",      align: "center", width: "130px" },
    { name: "status",          align: "center", width: "120px" },
    { name: "actions",         align: "right",  width: "190px" },
  ];

  const rows = exceptions.map((txn) => ({
    hasBorder: true,
    date: <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{fmtDate(txn.transaction_date)}</VuiTypography>,
    counterparty: (
      <VuiBox>
        <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
          {txn.counterparty?.name || "Unknown"}
        </VuiTypography>
        <VuiTypography variant="caption" display="block" sx={{ color: TC.tertiary,  opacity: 0.7 }}>
          {(txn.counterparty?.reference_text || "").slice(0, 60)}
        </VuiTypography>
      </VuiBox>
    ),
    amount: (
      <VuiBox textAlign="right">
        <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
          {fmtAmount(txn.amount, txn.currency)}
        </VuiTypography>
        <VuiTypography variant="caption" display="block"
          sx={{ color: txn.direction === "credit" ? "#01b574" : "#f44336", fontSize: "0.7rem" }}>
          {txn.direction === "credit" ? "▲ In" : "▼ Out"}
        </VuiTypography>
      </VuiBox>
    ),
    "ai suggestion": txn.suggested_debit_account ? (
      <VuiBox>
        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Dr {accountLabel(txn.suggested_debit_account)}</VuiTypography>
        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Cr {accountLabel(txn.suggested_credit_account)}</VuiTypography>
      </VuiBox>
    ) : (
      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Not categorised</VuiTypography>
    ),
    confidence: <ConfidenceBadge score={txn.confidence_score} />,
    status:     <StatusBadge status={txn.categorisation_status} />,
    actions: (
      <VuiBox display="flex" gap={0.75} justifyContent="flex-end">
        <Tooltip title="Approve AI suggestion and post to ledger">
          <span>
            <VuiButton size="small" color="success"
              sx={{ fontSize: "0.7rem", px: 1.2, py: 0.4, minWidth: 0 }}
              onClick={() => handleApprove(txn)}
              disabled={!txn.suggested_debit_account}>
              Approve
            </VuiButton>
          </span>
        </Tooltip>
        <Tooltip title="Override with correct accounts">
          <span>
            <VuiButton size="small" color="info"
              sx={{ fontSize: "0.7rem", px: 1.2, py: 0.4, minWidth: 0 }}
              onClick={() => setOverrideTxn(txn)}>
              Override
            </VuiButton>
          </span>
        </Tooltip>
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        <Grid container spacing={2} mb={3}>
          {[
            { label: "Needs Review",      value: pendingCount,      color: "#f44336" },
            { label: "Flagged for Check", value: flaggedCount,      color: TC.linkHover },
            { label: "Total Exceptions",  value: exceptions.length, color: TC.midtone },
          ].map(({ label, value, color }) => (
            <Grid item xs={12} md={4} key={label}>
              <Card sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", p: 2 }}>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{label}</VuiTypography>
                <VuiTypography variant="h4" fontWeight="bold" sx={{ color }}>{value}</VuiTypography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <VuiBox p={3}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
              <VuiBox>
                <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Exception Queue</VuiTypography>
                <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
                  Transactions requiring review — sorted by lowest confidence first.
                </VuiTypography>
              </VuiBox>
              <VuiButton color="info" size="small" onClick={load}>↻ Refresh</VuiButton>
            </VuiBox>

            {successMsg && (
              <VuiBox mb={2} p={1.5} borderRadius="8px"
                sx={{ background: "rgba(1,181,116,0.12)", border: "1px solid rgba(1,181,116,0.3)" }}>
                <VuiTypography variant="caption" color="success">{successMsg}</VuiTypography>
              </VuiBox>
            )}

            {loadError && (
              <VuiTypography color="error" variant="caption" display="block" mb={2}>{loadError}</VuiTypography>
            )}

            {isLoading ? (
              <LoadingSpinner size="sm" label="Loading exceptions…" />
            ) : exceptions.length === 0 ? (
              <VuiBox textAlign="center" py={5}>
                <VuiTypography variant="h5" mb={1} sx={{ color: TC.primary }}>✅ No exceptions</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                  All transactions have been categorised. New ones will appear here when they need review.
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox sx={{
                "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                "& .MuiTableRow-root:not(:last-child) td": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}`,
                },
              }}>
                <Table columns={columns} rows={rows} />
              </VuiBox>
            )}
          </VuiBox>
        </Card>
      </VuiBox>

      <OverrideDialog
        open={Boolean(overrideTxn)}
        onClose={() => setOverrideTxn(null)}
        txn={overrideTxn}
        tenantId={tenantId}
        onSaved={(id) => { setOverrideTxn(null); flash("Correction applied and posted to ledger."); load(); }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Exceptions;

